import { inject, Injectable } from '@angular/core';
import { LoggableAPI } from '../models/protocols/loggable-api';
import { ApiClient } from './api-client';
import { MemberAccount } from '../models/transactions/member-account';
import { catchError, delay, map } from 'rxjs/operators';
import { defaultIfEmpty, Observable, of, throwError, timer } from 'rxjs';
import { ODataResponse } from '../models/protocols/odata-response';
import { MemberAccountFundAllocation } from '../models/transactions/member-account-fund-allocation';
import { MemberAccountFundDetails } from '../models/transactions/member-account-fund-details';
import { MemberAccountContributionBalance } from '../models/transactions/member-account-contribution-balance';
import { MemberAccountBalanceByLockedStatus } from '../models/transactions/member-account-balance-by-locked-status';
import { Endpoints } from './endpoints';
import { ODataQueryOptions } from '../models/shared/odata-query-options';
import { CustomError } from '../models/shared/custom-error';
import { InvestmentInstructions } from '../models/transactions/investment-instructions';
import { AccountTypeIdEnum } from '../models/enum/shared/account-type-id.enum';
import { MemberAccountBalanceByFund } from '../models/transactions/member-account-balance-by-fund';
import { VbWithdrawalAmountRequest } from '../models/shared/requests/vb-withdrawal-amount-request';
import { VbWithdrawalAmount } from '../models/shared/vb-withdrawal-amount';
import { VariableBenefitAccountDetails } from '../models/transactions/variable-benefit-account-details';
import { VbAccountActivityBreakdown } from '../models/transactions/vb-account-activity-breakdown';
import { LockedUnlockedBalance } from '../models/shared/locked-unlocked-balance';
import { WithdrawalInstruction } from '../models/submissions/withdrawal-instruction';
import { VbUpcomingPayment } from '../models/transactions/vb-upcoming-payment';
import { MemberSubmission } from '../models/submissions/member-submission';
import { VariableBenefitSummary } from '../models/submissions/variable-benefit-summary';
import { MonthlyPensionSingleLifeAnnuityCalculation } from '../models/shared/monthly-pension-single-life-annuity-calculation';
import { MonthlyPensionJointLifeAnnuityCalculation } from '../models/shared/monthly-pension-joint-life-annuity-calculation';
import { AnnuityOperationalConversionRate } from '../models/shared/annuity-operational-conversion-rate';
import { CreateMonthlyPensionSingleLifeAnnuityCalculationRequest } from '../views/records/components/members/ip-member-record/components/ip-member-record-create-monthly-pension-account/requests/create-monthly-pension-single-life-annuity-calculation-request';
import { CreateMonthlyPensionJointLifeAnnuityCalculationRequest } from '../views/records/components/members/ip-member-record/components/ip-member-record-create-monthly-pension-account/requests/create-monthly-pension-joint-life-annuity-calculation-request';
import { MonthlyPensionAccountDetails } from '../models/transactions/monthly-pension-account-details';
import { MonthlyPensionAccountActionResponse } from '../models/transactions/monthly-pension-account-action-response';
import { MonthlyPensionEntitlement } from '../models/transactions/monthly-pension-entitlement';
import { IpTerminatePaymentInstructionRequest } from '../views/shared/components/monthly-pension-account-payment-instruction-details-card/requests/ip-terminate-payment-instruction-request';
import { IpEngageJlsPropertiesRequest } from '../views/shared/components/monthly-pension-account-payment-instruction-details-card/requests/ip-engage-jls-properties-request';
import { IpSplitMonthlyPensionRequest } from '../views/records/components/members/ip-member-record/components/ip-member-record-split-pension/requests/ip-split-monthly-pension-request';
import { JurisdictionCommutableThresholdWrapper } from '../models/shared/jurisdiction-commutable-threshold-wrapper';
import { JurisdictionCommutableThresholdRequest } from '../models/shared/requests/jurisdiction-commutable-threshold-request';
import { GoverningJurisdictionType } from '../models/employers/employer-history/types/governing-jurisdiction-type';
import { GoverningJurisdictionTypeId } from '../models/enum/shared/governing-jurisdiction-type-id';
import { IpUpdateDepositInstructionsRequest } from '../views/records/components/members/ip-member-record/components/ip-member-record-payments/requests/ip-update-deposit-instructions-request';
import { MpUpdateDepositInstructionsRequest } from '../views/accounts/member-portal/components/mp-view-accounts/mp-view-accounts-payments/requests/mp-update-deposit-instructions-request';
import { MemberAccountStatusIdEnum } from '../models/transactions/enum/member-account-status-id.enum';
import { mockAttachStatusChange, mockChangeAccountStatus } from './account-api.mocks';
import { IpCreateTransferredInAccountRequest } from '../features/create-transfer-in-account/models/ip-create-transferred-in-account-request';
import { IpCreateTransferredInAccountResponse } from '../features/create-transfer-in-account/models/ip-create-transferred-in-account-response';
import {
  IpSpousalVariableBenefitSourceAccounts,
  SpousalVariableBenefitSourceAccount
} from '../features/spousal-variable-benefit-rollover/models/ip-spousal-variable-benefit-source-accounts';
import { MemberUserSearchResult } from '../models/users/dto/member-user-search-result';
import {
  mockAttachSpousalRolloverAccount,
  mockAttachSpousalRollovers,
  mockCreateSpousalVariableBenefitAccounts,
  mockFindCreatedSpousalAccount,
  mockGetSpousalVariableBenefitAccountDetails,
  mockGetSpousalVariableBenefitActivityBreakdown,
  mockGetSpousalVariableBenefitFundAllocation,
  mockGetSpousalVariableBenefitInvestmentInstructions,
  mockGetSpousalVariableBenefitSourceAccounts
} from './spousal-variable-benefit-api.mocks';
import { MemberRecord } from '../models/users/dto/member-record';

/** Stands in for the network while the account status backend is built, so loading states behave. */
const MOCK_LATENCY_MS = 250;

export interface MemberAccountsFilter {
  accountIds?: number[];
  excludeStruck?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AccountAPI implements LoggableAPI {
  private apiClient = inject(ApiClient);

  serviceName = 'AccountAPI';

  public ipGetMemberAccounts(
    memberId: number,
    { accountIds, excludeStruck }: MemberAccountsFilter = {}
  ): Observable<ODataResponse<MemberAccount>> {
    const url = Endpoints.ipGetMemberAccounts(memberId);
    const oDataQueryOptions = new ODataQueryOptions();
    oDataQueryOptions.setExpand(
      'Type',
      'GoverningJurisdiction, Status, DefinedContributionAccountDetails, VariableBenefitAccountDetails, MonthlyPensionAccountDetails($expand=AnnuityGuaranteePeriod, AnnuityType)'
    );
    const filters: string[] = [];
    if (accountIds?.length) {
      filters.push(`Id in (${accountIds.join(',')})`);
    }
    if (excludeStruck) {
      filters.push(`StatusId ne ${MemberAccountStatusIdEnum.Struck}`);
    }
    if (filters.length) {
      oDataQueryOptions.setFilter(filters.join(' and '));
    }
    return this.apiClient.getOdata(url, MemberAccount, oDataQueryOptions).pipe(
      // TODO: CSS-3838 delete once the reworked spousal VB endpoints land and the reads return spousalRolloverAccount
      map(response => ({
        ...response,
        value: mockAttachSpousalRollovers(memberId, response.value, accountIds)
      })),
      map(response => this.attachStatusChanges(response)),
      map(response => (excludeStruck ? this.withoutStruck(response) : response)),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberAccountsByAccountType(
    memberId: number,
    accountTypeId: AccountTypeIdEnum,
    { excludeStruck }: Pick<MemberAccountsFilter, 'excludeStruck'> = {}
  ): Observable<ODataResponse<MemberAccount>> {
    const url = Endpoints.ipGetMemberAccounts(memberId);
    const oDataQueryOptions = new ODataQueryOptions();
    oDataQueryOptions.setExpand('Type', 'GoverningJurisdiction');
    const filters = [`TypeId eq ${accountTypeId}`];
    if (excludeStruck) {
      filters.push(`StatusId ne ${MemberAccountStatusIdEnum.Struck}`);
    }
    oDataQueryOptions.setFilter(filters.join(' and '));
    return this.apiClient.getOdata(url, MemberAccount, oDataQueryOptions).pipe(
      map(response => this.attachStatusChanges(response)),
      map(response => (excludeStruck ? this.withoutStruck(response) : response)),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberVariableBenefitAccountsWithPaymentDetails(
    memberId: number,
    { excludeStruck }: Pick<MemberAccountsFilter, 'excludeStruck'> = {}
  ): Observable<ODataResponse<MemberAccount>> {
    const url = Endpoints.ipGetMemberAccounts(memberId);
    const oDataQueryOptions = new ODataQueryOptions();
    const filters = [`TypeId eq ${AccountTypeIdEnum.VariableBenefit}`];
    if (excludeStruck) {
      filters.push(`StatusId ne ${MemberAccountStatusIdEnum.Struck}`);
    }
    oDataQueryOptions.setFilter(filters.join(' and '));
    oDataQueryOptions.setExpand(
      'Type',
      'GoverningJurisdiction',
      'Status',
      'VariableBenefitAccountDetails($expand=ScheduledRecurringPaymentInstruction($expand=PaymentFrequency),ActiveRecurringPaymentInstruction($expand=PaymentFrequency),ScheduledLumpSumPaymentInstruction)'
    );
    return this.apiClient.getOdata(url, MemberAccount, oDataQueryOptions).pipe(
      map(response => this.attachStatusChanges(response)),
      map(response => (excludeStruck ? this.withoutStruck(response) : response)),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetAccounts(accountTypeId?: AccountTypeIdEnum): Observable<ODataResponse<MemberAccount>> {
    const url = Endpoints.mpGetAccounts();
    const oDataQueryOptions = new ODataQueryOptions();
    oDataQueryOptions.setExpand(
      'Type',
      'GoverningJurisdiction, Status, DefinedContributionAccountDetails, VariableBenefitAccountDetails, MonthlyPensionAccountDetails($expand=AnnuityGuaranteePeriod, AnnuityType)'
    );
    if (accountTypeId) {
      oDataQueryOptions.setFilter(`TypeId eq ${accountTypeId}`);
    }
    return this.apiClient.getOdata(url, MemberAccount, oDataQueryOptions).pipe(
      map(response => this.attachStatusChanges(response)),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getInternalMemberAccount(memberId: number, accountId: number): Observable<MemberAccount> {
    // TODO: CSS-3840 delete once the reworked spousal VB endpoints land and create returns real accounts
    const spousalAccount = mockFindCreatedSpousalAccount(accountId);
    if (spousalAccount) {
      return timer(MOCK_LATENCY_MS).pipe(map(() => mockAttachStatusChange(spousalAccount)));
    }
    const url = Endpoints.getInternalMemberAccount(memberId, accountId);
    const oDataQueryOptions = new ODataQueryOptions();
    oDataQueryOptions.setExpand(
      'Type',
      'GoverningJurisdiction, Status, DefinedContributionAccountDetails, VariableBenefitAccountDetails, MonthlyPensionAccountDetails($Expand=AnnuityType, AnnuityGuaranteePeriod)'
    );
    return this.apiClient.getOdataObj<MemberAccount>(url, MemberAccount, oDataQueryOptions).pipe(
      map(mockAttachStatusChange),
      // TODO: CSS-3841 delete once the reworked spousal VB endpoints land and the read returns spousalRolloverAccount
      map(mockAttachSpousalRolloverAccount),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberAccountInvestmentInstructions(
    memberId: number,
    accountIds: number[]
  ): Observable<InvestmentInstructions[]> {
    // TODO: CSS-3840 delete once the reworked spousal VB endpoints land and create returns real accounts
    const spousalAccount = accountIds.length === 1 ? mockFindCreatedSpousalAccount(accountIds[0]) : null;
    if (spousalAccount) {
      return timer(MOCK_LATENCY_MS).pipe(
        map(() => mockGetSpousalVariableBenefitInvestmentInstructions(spousalAccount))
      );
    }
    const url = Endpoints.ipGetMemberAccountInvestmentInstructions(memberId, accountIds);
    return this.apiClient.getArr<InvestmentInstructions>(InvestmentInstructions, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetAccountInvestmentInstructions(accountIds: number[]): Observable<InvestmentInstructions[]> {
    const url = Endpoints.mpGetAccountInvestmentInstructions(accountIds);
    return this.apiClient.getArr<InvestmentInstructions>(InvestmentInstructions, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getMemberAccount(accountId: number): Observable<MemberAccount> {
    const url = Endpoints.getMemberAccount(accountId);
    const oDataQueryOptions = new ODataQueryOptions();
    oDataQueryOptions.setExpand(
      'Type',
      'GoverningJurisdiction, Status, DefinedContributionAccountDetails, VariableBenefitAccountDetails, MonthlyPensionAccountDetails($Expand=AnnuityType, AnnuityGuaranteePeriod)'
    );
    return this.apiClient.getOdataObj<MemberAccount>(url, MemberAccount, oDataQueryOptions).pipe(
      map(mockAttachStatusChange),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getMemberAccountBalanceByLockedStatus(accountId: number): Observable<MemberAccountBalanceByLockedStatus> {
    return of(MemberAccountBalanceByLockedStatus.generateFake()).pipe(delay(1000));
  }

  public mpGetAccountAllocationOfFunds(
    accountIds: number[],
    excludeCash: boolean
  ): Observable<MemberAccountFundAllocation[]> {
    const url = Endpoints.mpGetAccountAllocationOfFunds();
    const params = `?accountIds=${accountIds.join(',')}&excludeCash=${excludeCash}`;
    return this.apiClient.getArr(MemberAccountFundAllocation, url + params).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberAccountFundAllocation(
    memberId: number,
    accountIds: number[],
    excludeCash: boolean
  ): Observable<MemberAccountFundAllocation[]> {
    // TODO: CSS-3840 delete once the reworked spousal VB endpoints land and create returns real accounts
    if (accountIds.length === 1 && mockFindCreatedSpousalAccount(accountIds[0])) {
      return timer(MOCK_LATENCY_MS).pipe(map(() => mockGetSpousalVariableBenefitFundAllocation()));
    }
    const url = Endpoints.ipGetMemberAccountAllocationOfFunds(memberId);
    const params = `?accountIds=${accountIds.join(',')}&excludeCash=${excludeCash}`;
    return this.apiClient.getArr(MemberAccountFundAllocation, url + params).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberAccountFundDetails(memberId: number, accountId: number): Observable<MemberAccountFundDetails[]> {
    // TODO: CSS-3840 delete once the reworked spousal VB endpoints land and create returns real accounts
    if (mockFindCreatedSpousalAccount(accountId)) {
      return timer(MOCK_LATENCY_MS).pipe(map(() => []));
    }
    const url = Endpoints.ipGetMemberAccountFundDetails(memberId, accountId);
    return this.apiClient.getArr(MemberAccountFundDetails, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getMemberAccountFundDetails(accountId: number): Observable<MemberAccountFundDetails[]> {
    const url = Endpoints.getMemberAccountFundDetails(accountId);
    return this.apiClient.getArr(MemberAccountFundDetails, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberVbAccountDetails(memberId: number, accountId: number): Observable<VariableBenefitAccountDetails> {
    // TODO: CSS-3840 delete once the reworked spousal VB endpoints land and create returns real accounts
    const spousalAccount = mockFindCreatedSpousalAccount(accountId);
    if (spousalAccount) {
      return timer(MOCK_LATENCY_MS).pipe(map(() => mockGetSpousalVariableBenefitAccountDetails(spousalAccount)));
    }
    const url = Endpoints.ipGetMemberVbAccountDetails(memberId, accountId);
    return this.apiClient.getObj(VariableBenefitAccountDetails, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetVbAccountDetails(accountId: number): Observable<VariableBenefitAccountDetails> {
    const url = Endpoints.mpGetVbAccountDetails(accountId);
    return this.apiClient.getObj(VariableBenefitAccountDetails, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberVbAccountActivityBreakdown(
    memberId: number,
    accountId: number
  ): Observable<VbAccountActivityBreakdown> {
    // TODO: CSS-3840 delete once the reworked spousal VB endpoints land and create returns real accounts
    if (mockFindCreatedSpousalAccount(accountId)) {
      return timer(MOCK_LATENCY_MS).pipe(map(() => mockGetSpousalVariableBenefitActivityBreakdown()));
    }
    const url = Endpoints.ipGetMemberVbAccountActivityBreakdown(memberId, accountId);
    return this.apiClient.getObj(VbAccountActivityBreakdown, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberVbAccountNewMoneyLimits(
    memberId: number,
    accountId: number,
    effectiveDate: string | null,
    newMoneyAmount: number
  ): Observable<VbWithdrawalAmount> {
    const url = Endpoints.ipGetMemberVbAccountNewMoneyLimits(memberId, accountId);
    return this.apiClient.postObj(VbWithdrawalAmount, url, { memberId, accountId, effectiveDate, newMoneyAmount }).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetVbAccountActivityBreakdown(accountId: number): Observable<VbAccountActivityBreakdown> {
    const url = Endpoints.mpGetVbAccountActivityBreakdown(accountId);
    return this.apiClient.getObj(VbAccountActivityBreakdown, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getInternalMemberAccountContributionBalances(
    memberId: number,
    accountId: number
  ): Observable<MemberAccountContributionBalance[]> {
    const url = Endpoints.getInternalMemberAccountContributionBalances(memberId, accountId);
    return this.apiClient.getArr(MemberAccountContributionBalance, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberBalancesByAccountAndFund(
    memberId: number,
    accountTypeId: AccountTypeIdEnum
  ): Observable<MemberAccountBalanceByFund[]> {
    const url = Endpoints.ipGetMemberBalancesByAccountAndFund(memberId, accountTypeId);
    return this.apiClient.getArr<MemberAccountBalanceByFund>(MemberAccountBalanceByFund, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getMemberAccountContributionBalances(accountId: number): Observable<MemberAccountContributionBalance[]> {
    const url = Endpoints.getMemberAccountContributionBalances(accountId);
    return this.apiClient.getArr(MemberAccountContributionBalance, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpConfirmDefaultInvestmentInstructions(): Observable<any> {
    const url = Endpoints.mpConfirmDefaultInvestmentInstructions();
    return this.apiClient.simpleBodylessPut(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getVbMinMaxWithdrawalAmounts(req: VbWithdrawalAmountRequest): Observable<VbWithdrawalAmount> {
    const url = Endpoints.getVbMinMaxWithdrawalAmounts();
    return this.apiClient.postObj(VbWithdrawalAmount, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetLockedUnlockedBalances(
    memberId: number,
    overrideGoverningJurisdictionId: GoverningJurisdictionTypeId | null = null
  ): Observable<LockedUnlockedBalance[]> {
    const url = Endpoints.ipGetLockedUnlockedBalances(memberId, overrideGoverningJurisdictionId);
    return this.apiClient.getArr(LockedUnlockedBalance, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCalculateJurisdictionCommutableThresholds(
    memberId: number,
    calculationDate: string
  ): Observable<JurisdictionCommutableThresholdWrapper> {
    const url = Endpoints.ipCalculateJurisdictionCommutableThresholds(memberId);
    const req: JurisdictionCommutableThresholdRequest = { memberId, calculationDate };
    return this.apiClient.postObj(JurisdictionCommutableThresholdWrapper, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  /** Emits null when the member has no final location, which the backend reports as a 404. */
  public ipGetMemberDefinedContributionGoverningJurisdiction(
    memberId: number
  ): Observable<GoverningJurisdictionType | null> {
    const url = Endpoints.ipGetMemberDefinedContributionGoverningJurisdiction(memberId);
    return this.apiClient.getObj(GoverningJurisdictionType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return err.code === 404 ? of(null) : throwError(() => err);
      })
    );
  }

  public ipGetMemberVariableBenefitWithdrawalInstructions(memberId: number): Observable<WithdrawalInstruction[]> {
    const url = Endpoints.ipGetMemberVariableBenefitWithdrawalInstructions(memberId);
    return this.apiClient.getArr(WithdrawalInstruction, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberUpcomingVbPayments(memberId: number, paymentCount: number): Observable<VbUpcomingPayment[]> {
    const url = Endpoints.ipGetMemberUpcomingVbPayments(memberId, paymentCount);
    return this.apiClient.getArr(VbUpcomingPayment, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberActiveFundingVbSubmission(memberId: number): Observable<MemberSubmission | null> {
    const url = Endpoints.ipGetMemberActiveFundingVbSubmission(memberId);
    return this.apiClient.getObj(MemberSubmission, url).pipe(
      catchError(e => {
        if (e.status === 404) {
          return of(null);
        }
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetActiveVbFundingSubmission(): Observable<MemberSubmission | null> {
    const url = Endpoints.mpGetActiveFundingVbSubmission();
    return this.apiClient.getObj(MemberSubmission, url).pipe(
      defaultIfEmpty(null),
      catchError(e => {
        if (e.status === 404) {
          return of(null);
        }
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetVariableBenefitSummary(): Observable<VariableBenefitSummary | null> {
    const url = Endpoints.mpGetVariableBenefitSummary();
    return this.apiClient.getObj(VariableBenefitSummary, url).pipe(
      catchError(e => {
        if (e.status === 404) {
          return of(null);
        }
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetUpcomingVbPayments(paymentCount: number): Observable<VbUpcomingPayment[]> {
    const url = Endpoints.mpGetUpcomingVbPayments(paymentCount);
    return this.apiClient.getArr(VbUpcomingPayment, url).pipe(
      catchError(e => {
        if (e.status === 404) {
          return of([]);
        }
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetVariableBenefitWithdrawalInstructions(
    excludeZeroPercentFunds: boolean
  ): Observable<WithdrawalInstruction[]> {
    const url = Endpoints.mpGetVariableBenefitWithdrawalInstructions(excludeZeroPercentFunds);
    return this.apiClient.getArr(WithdrawalInstruction, url).pipe(
      catchError(e => {
        if (e.status === 404) {
          return of([]);
        }
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Monthly Pensions

  public ipCreateSingleLifeAnnuityCalculation(
    req: CreateMonthlyPensionSingleLifeAnnuityCalculationRequest
  ): Observable<MonthlyPensionSingleLifeAnnuityCalculation> {
    const url = Endpoints.ipCreateSingleLifeAnnuityCalculation();
    return this.apiClient.postObj(MonthlyPensionSingleLifeAnnuityCalculation, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateJointLifeAnnuityCalculation(
    req: CreateMonthlyPensionJointLifeAnnuityCalculationRequest
  ): Observable<MonthlyPensionJointLifeAnnuityCalculation> {
    const url = Endpoints.ipCreateJointLifeAnnuityCalculation();
    return this.apiClient.postObj(MonthlyPensionJointLifeAnnuityCalculation, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetAnnuityOperationalConversionRateForDate(date: string): Observable<AnnuityOperationalConversionRate> {
    const url = Endpoints.ipGetAnnuityOperationalConversionRateForDate(date);
    return this.apiClient.getObj(AnnuityOperationalConversionRate, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberMonthlyPensionAccountDetails(
    memberId: number,
    accountId: number
  ): Observable<MonthlyPensionAccountDetails> {
    return this.apiClient
      .getObj(MonthlyPensionAccountDetails, Endpoints.ipGetMemberMonthlyPensionAccountDetails(memberId, accountId))
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public mpGetMonthlyPensionAccountDetails(accountId: number): Observable<MonthlyPensionAccountDetails> {
    return this.apiClient
      .getObj(MonthlyPensionAccountDetails, Endpoints.mpGetMonthlyPensionAccountDetails(accountId))
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public ipGetMemberMonthlyPensionEntitlements(memberId: number): Observable<MonthlyPensionEntitlement[]> {
    const url = Endpoints.ipGetMemberMonthlyPensionEntitlements(memberId);
    return this.apiClient.getArr(MonthlyPensionEntitlement, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetMonthlyPensionEntitlements(): Observable<MonthlyPensionEntitlement[]> {
    const url = Endpoints.mpGetMonthlyPensionEntitlements();
    return this.apiClient.getArr(MonthlyPensionEntitlement, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateMonthlyPensionPaymentInstructionDepositInstructions(
    request: IpUpdateDepositInstructionsRequest
  ): Observable<MonthlyPensionAccountDetails> {
    const url = Endpoints.ipUpdateMonthlyPensionPaymentInstructionDepositInstructions(
      request.memberId,
      request.accountId,
      request.paymentInstructionId
    );
    return this.apiClient.putObj(MonthlyPensionAccountDetails, url, request).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpUpdateMonthlyPensionPaymentInstructionDepositInstructions(
    request: MpUpdateDepositInstructionsRequest
  ): Observable<MonthlyPensionAccountDetails> {
    const url = Endpoints.mpUpdateMonthlyPensionPaymentInstructionDepositInstructions(
      request.accountId,
      request.paymentInstructionId
    );
    return this.apiClient.putObj(MonthlyPensionAccountDetails, url, request).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipTerminateMonthlyPensionPaymentInstruction(
    request: IpTerminatePaymentInstructionRequest
  ): Observable<MonthlyPensionAccountActionResponse> {
    const url = Endpoints.ipTerminateMonthlyPensionPaymentInstruction(
      request.memberId,
      request.accountId,
      request.paymentInstructionId
    );
    return this.apiClient.putObj(MonthlyPensionAccountActionResponse, url, request).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipEngageMonthlyPensionJointLifeSurvivorship(
    request: IpEngageJlsPropertiesRequest
  ): Observable<MonthlyPensionAccountActionResponse> {
    const url = Endpoints.ipEngageMonthlyPensionJointLifeSurvivorship(request.memberId, request.accountId);
    return this.apiClient.postObj(MonthlyPensionAccountActionResponse, url, request).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSplitMonthlyPension(request: IpSplitMonthlyPensionRequest): Observable<MonthlyPensionAccountActionResponse> {
    const url = Endpoints.ipSplitMonthlyPension(request.memberId, request.accountId);
    return this.apiClient.postObj(MonthlyPensionAccountActionResponse, url, request).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateTransferredInAccount(
    request: IpCreateTransferredInAccountRequest
  ): Observable<IpCreateTransferredInAccountResponse> {
    const url = Endpoints.ipCreateTransferredInAccount(request.memberId);
    return this.apiClient.postObj(IpCreateTransferredInAccountResponse, url, request).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // TODO: CSS-3837 call GET internal/members/{memberId}/spousalvariablebenefit/sourceaccounts with only the member's id
  // once the reworked spousal variable benefit endpoints are deployed
  public ipGetSpousalVariableBenefitSourceAccounts(
    member: MemberRecord,
    accounts: MemberAccount[]
  ): Observable<IpSpousalVariableBenefitSourceAccounts> {
    return timer(MOCK_LATENCY_MS).pipe(map(() => mockGetSpousalVariableBenefitSourceAccounts(member, accounts)));
  }

  // TODO: CSS-3838 call POST internal/members/{memberId}/spousalvariablebenefitaccounts with
  // { memberId, successorMemberId, sourceAccountIds }, taking only the member's id, once the reworked spousal variable
  // benefit endpoints are deployed
  public ipCreateSpousalVariableBenefitAccounts(
    member: MemberRecord,
    successor: MemberUserSearchResult,
    sourceAccounts: SpousalVariableBenefitSourceAccount[]
  ): Observable<void> {
    return timer(MOCK_LATENCY_MS).pipe(
      map(() => mockCreateSpousalVariableBenefitAccounts(member, successor, sourceAccounts))
    );
  }

  // TODO: CSS-3941 call PUT internal/members/{memberId}/accounts/{accountId}/suspend once the backend exists
  public ipSuspendMemberAccount(memberId: number, accountId: number): Observable<void> {
    return this.mockChangeStatus(accountId, MemberAccountStatusIdEnum.Suspended);
  }

  // TODO: CSS-3941 call PUT internal/members/{memberId}/accounts/{accountId}/liftsuspension once the backend exists
  public ipLiftMemberAccountSuspension(memberId: number, accountId: number): Observable<void> {
    return this.mockChangeStatus(accountId, MemberAccountStatusIdEnum.Active);
  }

  // TODO: CSS-3941 call PUT internal/members/{memberId}/accounts/{accountId}/strike once the backend exists
  public ipStrikeMemberAccount(memberId: number, accountId: number): Observable<void> {
    return this.mockChangeStatus(accountId, MemberAccountStatusIdEnum.Struck);
  }

  // A failed change waits out the latency too, so the modal's loading state shows before the error toast.
  private mockChangeStatus(accountId: number, statusId: MemberAccountStatusIdEnum): Observable<void> {
    return timer(MOCK_LATENCY_MS).pipe(map(() => mockChangeAccountStatus(accountId, statusId)));
  }

  // TODO: CSS-3941 delete once the account reads return the changed status and statusChangedDate
  private attachStatusChanges(response: ODataResponse<MemberAccount>): ODataResponse<MemberAccount> {
    return { ...response, value: response.value.map(mockAttachStatusChange) };
  }

  // TODO: CSS-3941 delete once the account reads return the changed status, when the StatusId filter alone is enough
  private withoutStruck(response: ODataResponse<MemberAccount>): ODataResponse<MemberAccount> {
    return { ...response, value: response.value.filter(account => !account.isStruck()) };
  }
}
